import { createContext, useContext } from 'react';
import type { OutboundMacroValues } from '@/utils/outboundUrlMacros';

/**
 * externalExitContext — WEB-2124 / WEB-2164: ЕДИНАЯ дверь ухода с квиза наружу.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ КОНТЕКСТ, А НЕ ПРОПС (тот же довод, что у `StoreLinksContext`):
 * уводящие блоки — «редирект» по обратному отсчёту и кнопка-ссылка — рисуются
 * где угодно в дереве экрана (страница → визард → экран → рендерер блоков).
 * Протаскивать обработчик пропсами через все слои значило бы менять подписи
 * четырёх компонентов ради двух типов блоков, а потом повторять то же на
 * пейволле.
 *
 * ЧТО ЭТА ДВЕРЬ ДЕЛАЕТ И ПОЧЕМУ ИМЕННО В ТАКОМ ПОРЯДКЕ. До WEB-2124 уход по
 * блоку не делал НИЧЕГО: страница уезжала, событие завершения не излучалось, а
 * всё, что накопилось в очереди событий, терялось вместе с выгрузкой. Порядок
 * строк здесь критичен и один на все ветки ухода:
 *   1) событие завершения ложится в очередь,
 *   2) очередь досылается маяком (`transport: 'beacon'`),
 *   3) браузер уходит.
 * Наоборот — маяк уедет без события, и завершение потеряется.
 *
 * ЗНАЧЕНИЕ ПО УМОЛЧАНИЮ — `null`, «ухода нет». Канва конструктора, пейволл и
 * любой рендер без провайдера получают прежнее поведение: блок уводит сам и
 * ничего не засчитывает. Выдуманного завершения квиза не бывает там, где квиза
 * нет.
 */
export interface ExternalExitContextValue {
  /**
   * БЛОК «РЕДИРЕКТ» по обратному отсчёту: засчитать завершение, дослать
   * очередь и увести человека — через нашу серверную точку ухода, если она
   * доступна (WEB-2164), иначе прямо по адресу.
   *
   * `target` — уже очищенный адрес (`safeHref`): он нужен как ЗАПАСНОЙ путь,
   * когда точку ухода собрать не из чего (превью черновика без проекта).
   */
  exitViaRedirectBlock: (blockId: string, target: string) => void;

  /**
   * КНОПКА-ССЫЛКА: только засчитать завершение и дослать очередь. Уводит сам
   * браузер — цель и вкладка кнопки не меняются (WEB-2129 про `target="_blank"`
   * — отдельная карточка), и через серверную точку ухода она НЕ идёт: это
   * элемент экрана, а не концовка квиза, и шаг «ушёл на оплату» по ней не
   * пишется (WEB-2164 п.6 — иначе шаг станет больше числа завершений).
   */
  markLinkExit: () => void;

  /**
   * WEB-2262 (решение владельца 18.09, вариант «а»): ЗНАЧЕНИЯ МЕТОК для адреса
   * кнопки-ссылки. Кнопка уходит мимо серверной точки (см. `markLinkExit`), и
   * подставить метки в её адрес может только браузер — иначе автор, вписавший
   * `?src={utm_source}`, отправляет покупателя с литеральными скобками и его
   * закупочная разметка ломается тихо.
   *
   * ЗНАЧЕНИЯ ДАЁТ ПЛЕЕР, А НЕ РЕНДЕРЕР. Гейт согласия и сигнала GPC живёт в
   * одном месте (`outboundMacroValues()` в QuizScreenWizard) на все ветки
   * ухода: своя копия гейта у кнопки разошлась бы с остальными молча (шрам
   * WEB-953). Отказал — значений нет вовсе, метки выбрасываются пустыми, а
   * сам переход остаётся: дойти до оплаты человек обязан.
   *
   * ПОЛЕ НЕОБЯЗАТЕЛЬНОЕ — кто уходом распоряжается, но метки не считает
   * (пейволл, канва конструктора), получает прежнее поведение: адрес уходит
   * как есть.
   */
  outboundMacroValues?: () => OutboundMacroValues;

  /**
   * WEB-3296: адрес серверной точки ухода для кнопки-ссылки (цель — id блока;
   * адрес сервер берёт из опубликованной версии и считает переход в денежную
   * запись, WEB-3295). Собирает плеер (квиза или пейволла), не рендерер;
   * `null` или нет поля (превью, канва) — кнопка уводит прямо, как раньше.
   */
  linkExitHref?: (blockId: string) => string | null;
}

const ExternalExitContext = createContext<ExternalExitContextValue | null>(
  null,
);

export const ExternalExitProvider = ExternalExitContext.Provider;

/** `null` вне плеера квиза — «ухода нет», блок ведёт себя как раньше. */
export function useExternalExit(): ExternalExitContextValue | null {
  return useContext(ExternalExitContext);
}
